import type {UserInfo} from "./user.interface";

/**
 * The response fields this factory actually reads — the narrowed union of the
 * login (`POST /api/v1/auth/login`) and whoami (`GET /api/v1/auth/whoami`)
 * shapes. Deliberately NOT either full response type: naming only what is read
 * keeps the two callers from having to agree on the fields it ignores.
 *
 * `username` is optional because whoami declares it so; `userUid` is nullable
 * because the login path resolves it by username and that lookup can, in
 * principle, come back empty.
 */
export interface UserInfoSource {
    userUid?: string | null;
    username?: string;
    token: {
        validUntil: string;
    };
}

/**
 * The ONE place either auth path constructs a `UserInfo`.
 *
 * It exists because the two paths are not symmetrical and treating them as
 * symmetrical has already gone wrong once in this codebase: `AuthenticateAction`
 * seeds `'employeeUid' => null` in its base result array and the success overlay
 * never overwrites it, so the login response returns null for it forever — while
 * both doc comments claim the two shapes are identical. A field added by hand at
 * two call sites drifts the same way. A field added here cannot.
 *
 * The concrete consequence for this feature: the hydration path returns early
 * when somebody is already logged in, so it never repairs an omission on the
 * login path. A uid added only to hydration would be present after a reload and
 * absent after a fresh login — which reads as a feature bug, not as missing
 * plumbing.
 *
 * `token.hash` is hardcoded `'cookie'` on both paths: the real token lives in an
 * httpOnly cookie the client cannot read, so the field is a marker rather than a
 * secret. Hardcoded once, here.
 */
export function buildUserInfo(source: UserInfoSource): UserInfo {
    return {
        uid: source.userUid ?? null,
        username: source.username ?? '',
        token: {
            hash: 'cookie',
            validUntil: source.token.validUntil,
        },
    };
}
